import { useEffect, useRef, useState } from 'react';
import { api } from './api.ts';

const TARGET_RATE = 24_000;
const SPEECH_RMS = 0.012;
const SILENCE_MS = 800;
const MIN_SPEECH_MS = 280;
const STOP_WAIT_MS = 1_600;
const MAX_QUEUED_FRAMES = 24;

type PhoneReply =
  | { t: 'ready' }
  | { t: 'delta'; text: string }
  | { t: 'final'; text: string }
  | { t: 'error'; message: string };

const WORKLET = `
class PcmCapture extends AudioWorkletProcessor {
  process(inputs) {
    const ch = inputs[0] && inputs[0][0];
    if (ch && ch.length) this.port.postMessage(ch);
    return true;
  }
}
registerProcessor('pcm-capture', PcmCapture);
`;

export interface Dictation {
  /** False until the gateway says a key is configured, so the button can hide. */
  available: boolean;
  listening: boolean;
  /** True after the user taps stop, until leftover audio is transcribed. */
  stopping: boolean;
  /** Heard but not yet committed. Displayed only; never part of the message. */
  interim: string;
  error: string | null;
  toggle: () => void;
  /** Drops the microphone and any audio not yet transcribed. */
  cancel: () => void;
}

interface Capture {
  stop: () => void;
}

/**
 * Dictation for the composer and the home voice-task FAB.
 *
 * The phone captures PCM and the gateway transcribes it with OpenAI
 * gpt-live-transcribe. Committed phrases go to `onText` as they land.
 * `onStopped` fires after a user-initiated stop, once leftover audio is
 * flushed — the home FAB uses that to file a task. Chat still does not
 * auto-submit: identifiers want a glance before they go to an agent.
 */
export function useDictation(onText: (phrase: string) => void, onStopped?: () => void): Dictation {
  const [available, setAvailable] = useState(false);
  const [listening, setListening] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [interim, setInterim] = useState('');
  const [error, setError] = useState<string | null>(null);

  const wanted = useRef(false);
  const finishing = useRef(false);
  const wsRef = useRef<WebSocket | null>(null);
  const captureRef = useRef<Capture | null>(null);
  const readyRef = useRef(false);
  const queueRef = useRef<ArrayBuffer[]>([]);
  const stopTimer = useRef<number | null>(null);
  const sink = useRef(onText);
  sink.current = onText;
  const stopped = useRef(onStopped);
  stopped.current = onStopped;

  useEffect(() => {
    void api
      .me()
      .then((me) => setAvailable(me.dictation === true))
      .catch(() => setAvailable(false));
  }, []);

  function halt(message: string | null): void {
    const finished = finishing.current && message === null;
    finishing.current = false;
    wanted.current = false;
    readyRef.current = false;
    queueRef.current = [];
    setListening(false);
    setStopping(false);
    setInterim('');
    if (message !== null) setError(message);
    if (stopTimer.current !== null) {
      clearTimeout(stopTimer.current);
      stopTimer.current = null;
    }
    captureRef.current?.stop();
    captureRef.current = null;
    const ws = wsRef.current;
    wsRef.current = null;
    if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
      ws.close();
    }
    if (finished) stopped.current?.();
  }

  function sendJson(msg: { t: 'commit' } | { t: 'stop' }): void {
    const ws = wsRef.current;
    if (ws?.readyState === WebSocket.OPEN) ws.send(JSON.stringify(msg));
  }

  function sendPcm(bytes: ArrayBuffer): void {
    const ws = wsRef.current;
    if (ws?.readyState === WebSocket.OPEN && readyRef.current) {
      ws.send(bytes);
      return;
    }
    const q = queueRef.current;
    if (q.length >= MAX_QUEUED_FRAMES) q.shift();
    q.push(bytes);
  }

  function flushQueue(): void {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    for (const frame of queueRef.current) ws.send(frame);
    queueRef.current = [];
  }

  async function begin(): Promise<void> {
    const url = new URL('/ws/dictation', location.href);
    url.protocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
    url.searchParams.set('lang', navigator.language || 'en');

    const ws = new WebSocket(url);
    ws.binaryType = 'arraybuffer';
    wsRef.current = ws;

    ws.onmessage = (ev: MessageEvent<string>) => {
      let msg: PhoneReply;
      try {
        msg = JSON.parse(String(ev.data)) as PhoneReply;
      } catch {
        return;
      }
      if (msg.t === 'ready') {
        readyRef.current = true;
        flushQueue();
        return;
      }
      if (msg.t === 'delta') {
        setInterim(msg.text.trim());
        return;
      }
      if (msg.t === 'final') {
        setInterim('');
        if (msg.text.trim()) sink.current(msg.text.trim());
        return;
      }
      if (msg.t === 'error') halt(msg.message);
    };

    ws.onclose = () => {
      if (wsRef.current !== ws) return;
      wsRef.current = null;
      if (wanted.current) halt('Dictation disconnected.');
      else halt(null);
    };
    ws.onerror = () => {
      if (wsRef.current === ws) halt('Dictation could not connect.');
    };

    try {
      const vad = new Vad();
      const capture = await openMic((samples, sampleRate) => {
        if (!wanted.current) return;
        const pcm = resampleToPcm16(samples, sampleRate, TARGET_RATE);
        if (pcm.length === 0) return;
        sendPcm(pcm.buffer.slice(pcm.byteOffset, pcm.byteOffset + pcm.byteLength) as ArrayBuffer);
        if (readyRef.current && vad.note(rms(samples), (samples.length / sampleRate) * 1000)) {
          sendJson({ t: 'commit' });
        }
      });
      if (!wanted.current) {
        capture.stop();
        return;
      }
      captureRef.current = capture;
    } catch (err) {
      halt(micError(err));
    }
  }

  const toggle = (): void => {
    if (wanted.current) {
      wanted.current = false;
      finishing.current = true;
      setListening(false);
      setStopping(true);
      captureRef.current?.stop();
      captureRef.current = null;
      sendJson({ t: 'stop' });
      if (stopTimer.current !== null) clearTimeout(stopTimer.current);
      stopTimer.current = window.setTimeout(() => halt(null), STOP_WAIT_MS);
      return;
    }
    if (finishing.current) return;
    if (!isSecureContext) {
      setError('Dictation needs HTTPS.');
      return;
    }
    setError(null);
    wanted.current = true;
    finishing.current = false;
    readyRef.current = false;
    queueRef.current = [];
    setStopping(false);
    setListening(true);
    void begin();
  };

  // Android freezes a backgrounded tab; a capture left running there holds
  // the microphone and its status icon until the phone comes back.
  useEffect(() => {
    const onHidden = (): void => {
      if (document.visibilityState === 'hidden' && wanted.current) halt(null);
    };
    document.addEventListener('visibilitychange', onHidden);
    return () => document.removeEventListener('visibilitychange', onHidden);
  }, []);

  useEffect(
    () => () => {
      wanted.current = false;
      captureRef.current?.stop();
      captureRef.current = null;
      wsRef.current?.close();
      wsRef.current = null;
    },
    [],
  );

  return {
    available,
    listening,
    stopping,
    interim,
    error,
    toggle,
    cancel: () => {
      finishing.current = false;
      halt(null);
    },
  };
}

class Vad {
  private speechMs = 0;
  private silentMs = 0;

  note(level: number, frameMs: number): boolean {
    if (level >= SPEECH_RMS) {
      this.speechMs += frameMs;
      this.silentMs = 0;
      return false;
    }
    if (this.speechMs < MIN_SPEECH_MS) {
      this.speechMs = 0;
      this.silentMs = 0;
      return false;
    }
    this.silentMs += frameMs;
    if (this.silentMs < SILENCE_MS) return false;
    this.speechMs = 0;
    this.silentMs = 0;
    return true;
  }
}

function rms(samples: Float32Array): number {
  let sum = 0;
  for (let i = 0; i < samples.length; i += 1) {
    const s = samples[i] ?? 0;
    sum += s * s;
  }
  return Math.sqrt(sum / Math.max(1, samples.length));
}

function floatToInt16(s: number): number {
  const x = Math.max(-1, Math.min(1, s));
  return x < 0 ? Math.round(x * 0x8000) : Math.round(x * 0x7fff);
}

function resampleToPcm16(input: Float32Array, inRate: number, outRate: number): Int16Array {
  if (inRate === outRate) {
    const out = new Int16Array(input.length);
    for (let i = 0; i < input.length; i += 1) out[i] = floatToInt16(input[i] ?? 0);
    return out;
  }
  const ratio = inRate / outRate;
  const outLen = Math.floor(input.length / ratio);
  const out = new Int16Array(outLen);
  for (let i = 0; i < outLen; i += 1) {
    const src = i * ratio;
    const i0 = Math.floor(src);
    const frac = src - i0;
    const a = input[i0] ?? 0;
    const b = input[i0 + 1] ?? a;
    out[i] = floatToInt16(a + (b - a) * frac);
  }
  return out;
}

async function openMic(onPcm: (samples: Float32Array, sampleRate: number) => void): Promise<Capture> {
  const stream = await navigator.mediaDevices.getUserMedia({
    audio: {
      channelCount: 1,
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
    },
  });
  const ctx = new AudioContext();
  await ctx.resume();
  const source = ctx.createMediaStreamSource(stream);
  const handle = (samples: Float32Array): void => onPcm(samples, ctx.sampleRate);

  let teardown: () => void;
  try {
    teardown = await attachWorklet(ctx, source, handle);
  } catch {
    teardown = attachProcessor(ctx, source, handle);
  }

  return {
    stop: () => {
      teardown();
      source.disconnect();
      for (const track of stream.getTracks()) track.stop();
      void ctx.close();
    },
  };
}

async function attachWorklet(
  ctx: AudioContext,
  source: MediaStreamAudioSourceNode,
  handle: (samples: Float32Array) => void,
): Promise<() => void> {
  const blob = new Blob([WORKLET], { type: 'application/javascript' });
  const url = URL.createObjectURL(blob);
  try {
    await ctx.audioWorklet.addModule(url);
  } finally {
    URL.revokeObjectURL(url);
  }
  const node = new AudioWorkletNode(ctx, 'pcm-capture');
  node.port.onmessage = (e: MessageEvent<Float32Array>) => handle(e.data);
  const mute = ctx.createGain();
  mute.gain.value = 0;
  source.connect(node);
  node.connect(mute);
  mute.connect(ctx.destination);
  return () => {
    node.port.onmessage = null;
    node.disconnect();
    mute.disconnect();
  };
}

function attachProcessor(
  ctx: AudioContext,
  source: MediaStreamAudioSourceNode,
  handle: (samples: Float32Array) => void,
): () => void {
  const processor = ctx.createScriptProcessor(4096, 1, 1);
  processor.onaudioprocess = (e) => {
    // The channel buffer is reused; copy before the next callback overwrites it.
    handle(e.inputBuffer.getChannelData(0).slice());
  };
  const mute = ctx.createGain();
  mute.gain.value = 0;
  source.connect(processor);
  processor.connect(mute);
  mute.connect(ctx.destination);
  return () => {
    processor.onaudioprocess = null;
    processor.disconnect();
    mute.disconnect();
  };
}

function micError(err: unknown): string {
  const name = err instanceof DOMException ? err.name : '';
  if (name === 'NotAllowedError' || name === 'SecurityError') {
    return 'Microphone blocked. Allow it in the site settings.';
  }
  if (name === 'NotFoundError') return 'No microphone found.';
  if (name === 'NotReadableError') return 'Microphone is already in use.';
  return err instanceof Error ? err.message : 'Could not open the microphone.';
}
